/**
 * Storage for the open-request tabs.
 *
 * Deliberately not inside the component that renders them: the API client clears these on
 * a 401, and an api → component import would drag React into the module every request
 * goes through.
 *
 * ⚠️ sessionStorage, not localStorage, and the difference is the point.
 *
 * These are shared clinical workstations. localStorage outlives the browser session, so
 * the next person to sit down would see which requests the last person had open — and a
 * subject on this system is free text somebody typed about their problem. sessionStorage
 * dies with the tab, and `clearTicketTabs` empties it on sign-out so it does not survive
 * even that long.
 *
 * A tab is a bookmark, never a grant: opening one fetches the ticket and re-checks access
 * server-side like any other navigation.
 */

const STORAGE_KEY = "tf-open-tickets";

/** Bounded, so a long shift does not accumulate fifty tabs nobody closes. */
export const MAX_TABS = 8;

/** Same-document writes do not raise `storage`, so listeners are told directly. */
export const TICKET_TABS_CHANGED = "tf-open-tickets-changed";

export interface TicketTab {
  number: string;
  displayNumber: number;
  subject: string;

  /**
   * Whether the requester is a VIP (D-34).
   *
   * ⚠️ Optional, because tabs written by an earlier build are already in sessionStorage and
   * must keep opening. Absent reads as "not marked", which is the safe direction: a tab that
   * loses its pill until the request is next opened is a smaller fault than one that vanishes.
   *
   * ⚠️ Presentation only, like every other use of this flag: it names a job title, grants
   * nothing and hides nothing. It is here because the mark stopping short of the tab strip is
   * the same gap as it stopping short of the request.
   */
  isRequesterVip?: boolean;
}

export function readTicketTabs(): TicketTab[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    // Validated rather than trusted. It is our own data, but it is data from outside the
    // running program, and a malformed entry should drop out rather than render.
    return parsed.filter(
      (t): t is TicketTab =>
        typeof t === "object" &&
        t !== null &&
        typeof (t as TicketTab).number === "string" &&
        typeof (t as TicketTab).displayNumber === "number" &&
        typeof (t as TicketTab).subject === "string",
    );
  } catch {
    return [];
  }
}

export function writeTicketTabs(tabs: TicketTab[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tabs));
  } catch {
    // Storage full, or blocked by policy. Tabs are a convenience; losing them must never
    // take the page down with them.
  }

  window.dispatchEvent(new Event(TICKET_TABS_CHANGED));
}

export function clearTicketTabs() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing worth doing */
  }

  window.dispatchEvent(new Event(TICKET_TABS_CHANGED));
}
